<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal-content">
      <h2 class="modal-title">New Template</h2>
      
      <div class="form-group">
        <label>Template Name</label>
        <input type="text" v-model="name" class="form-input" placeholder="Enter a name for this template">
      </div>

      <div class="form-group">
        <label>Type</label>
        <select v-model="type" class="form-select">
          <option value="member">Member Notification</option>
          <option value="speaker">Speaker Notification</option>
        </select>
      </div>

      <div class="form-group">
        <label>Subject</label>
        <input type="text" v-model="subject" class="form-input" placeholder="Email subject line">
      </div>

      <div class="form-group">
        <label>Body</label>
        <textarea 
          ref="bodyInput"
          v-model="body" 
          class="form-textarea" 
          rows="12"
        ></textarea>
      </div>

      <div class="variables-section">
        <h3>Available Variables:</h3>
        <div class="variables-grid">
          <div class="variable-column">
            <h4>Event Details</h4>
            <div v-for="(value, key) in event" :key="`event.${key}`"
                 class="variable-item" @click="insertVariable(`event.${key}`)">
              {{value}}
            </div>
          </div>

          <div class="variable-column">
            <h4>Member Variables</h4>
            <div v-for="(value, key) in member" :key="`member.${key}`"
                 class="variable-item" @click="insertVariable(`member.${key}`)">
              {{value}}
            </div>
          </div>

          <div class="variable-column" v-if="type === 'member'">
            <h4>Check-in Links</h4>
            <div v-for="(value, key) in checkinLinks" :key="`checkin.${key}`"
                 class="variable-item" @click="insertVariable(`${key}`)">
              {{value}}
            </div>
          </div>

          <div class="variable-column" v-if="type === 'speaker'">
            <h4>Speaker Variables</h4>
            <div v-for="(value, key) in speaker" :key="`speaker.${key}`"
                 class="variable-item" @click="insertVariable(`speaker.${key}`)">
              {{value}}
            </div>
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button @click="previewTemplate" class="btn btn-secondary">Preview</button>
        <button @click="$emit('close')" class="btn btn-secondary">Cancel</button>
        <button @click="saveTemplate" class="btn btn-primary">Save Template</button>
      </div>
    </div>
  </div>

  <EmailPreviewModal
    v-if="showPreview"
    :type="type"
    :subject="previewSubject"
    :parsed-body="previewBody"
    :event="previewEvent"
    :format="previewFormat"
    @close="showPreview = false"
  />
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  z-index: 1001;
}

.modal-title {
  text-align: left;
  margin-bottom: 1.5rem;
  color: #2c5282;
  font-size: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-select,
.form-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

.form-input {
  min-width: 400px;
}

.form-textarea {
  width: 100%;
  min-height: 200px;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-family: inherit;
}

.variables-section {
  margin: 1rem 0;
  text-align: left;
}

.variables-section h3 {
  font-size: 0.9rem;
  color: #4a5568;
  margin-bottom: 0.5rem;
}

.variables-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  background: #f7fafc;
  padding: 1rem;
  border-radius: 4px;
}

.variable-column h4 {
  font-size: 0.8rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.variable-item {
  font-size: 0.75rem;
  padding: 0.25rem 0;
  cursor: pointer;
  color: #4a5568;
}

.variable-item:hover {
  color: #2b6cb0;
  text-decoration: underline;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: #2b6cb0;
  color: white;
}

.btn-primary:hover {
  background: #2c5282;
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #cbd5e0;
}
</style>

<script>
import EmailPreviewModal from './EmailPreviewModal.vue';
import { eventService } from '../services/api';
import { meetingFormatService } from '../services/meetingFormatService';

export default {
  name: 'EmailTemplateModal',
  components: {
    EmailPreviewModal
  },
  emits: ['close', 'save'],
  inject: ['closeModal'],
  props: {
    template: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      name: this.template?.name || '',
      type: this.template?.type || 'member',
      subject: this.template?.subject || '',
      body: this.template?.body || '',
      showPreview: false,
      previewSubject: '',
      previewBody: '',
      previewEvent: null,
      previewFormat: null,
      event: {
        date: '{{event.date}}',
        time: '{{event.time}}',
        venue_name: '{{event.venue_name}}',
        venue_address: '{{event.venue_address}}',
        event_type: '{{event.event_type}}',
        agenda: '{{event.agenda}}'
      },
      member: {
        first_name: '{{member.first_name}}',
        display_name: '{{member.display_name}}',
        company_name: '{{member.company_name}}'
      },
      speaker: {
        first_name: '{{speaker.first_name}}',
        name: '{{speaker.name}}',
        topic: '{{speaker.topic}}',
        description: '{{speaker.description}}',
        time: '{{speaker.time}}',
        duration: '{{speaker.duration}}'
      },
      checkinLinks: {
        checkin_link: '{{checkin_link}}'
      }
    }
  },
  watch: {
    template(newTemplate) {
      if (newTemplate) {
        this.name = newTemplate.name;
        this.type = newTemplate.type;
        this.subject = newTemplate.subject;
        this.body = newTemplate.body;
      }
    }
  },
  mounted() {
    document.body.style.overflow = 'hidden';
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
  methods: {
    async previewTemplate() {
      try {
        // Get a sample event with format for preview
        const events = await eventService.getAllEvents();
        const sampleEvent = events.find(e => e.format_id && e.sessions?.length > 0);
        
        if (sampleEvent) {
          const [eventDetails, format, venue] = await Promise.all([
            eventService.getEventDetails(sampleEvent.id),
            meetingFormatService.getFormatById(sampleEvent.format_id),
            eventService.getAllVenues().then(venues => 
              venues.find(v => v.id === sampleEvent.venue_id)
            )
          ]);
          
          // Combine event details with venue information
          this.previewEvent = {
            ...eventDetails,
            venue_name: venue?.name || 'Venue Name Not Found',
            venue_address: venue?.address || 'Venue Address Not Found'
          };
          this.previewFormat = format;

          // Create sample data for preview
          const sampleData = {
            event: {
              date: new Date(sampleEvent.event_date).toLocaleString('en-AU', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                timeZone: 'Australia/Brisbane'
              }),
              time: new Date(sampleEvent.event_date).toLocaleTimeString('en-AU', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: 'Australia/Brisbane'
              }),
              venue_name: venue?.name || 'Sample Venue',
              venue_address: venue?.address || '123 Sample St',
              event_type: 'Regular Meeting',
              agenda: '(Sample Agenda)'
            },
            member: {
              first_name: 'John',
              display_name: 'John Smith',
              company_name: 'Sample Company'
            },
            checkin_link: `${window.location.origin}/checkin/sample-token-abc123`,
            speaker: {
              first_name: 'Jane',
              name: 'Jane Doe',
              topic: 'Sample Topic',
              description: 'This is a sample description of the speaking topic.',
              time: '7:25 AM',
              duration: '40 minutes'
            }
          };

          // Replace variables in subject and body
          let previewSubject = this.subject;
          let previewBody = this.body;

          // Helper function to replace all variables
          const replaceVariables = (text) => {
            // Simple test first
            if (text.includes('{{checkin_link}}')) {
              text = text.replace(/\{\{checkin_link\}\}/g, sampleData.checkin_link);
            }
            
            // Handle other variables
            return text.replace(/\{\{([\w._]+)\}\}/g, (match, path) => {
              // Handle direct variables (like checkin_link) and nested variables (like member.first_name)
              if (path.includes('.')) {
                const keys = path.split('.');
                let value = sampleData;
                for (const key of keys) {
                  value = value?.[key];
                }
                return value || match;
              } else {
                // Direct variable lookup
                return sampleData[path] || match;
              }
            });
          };

          // Apply replacements
          previewSubject = replaceVariables(previewSubject);
          previewBody = replaceVariables(previewBody);

          // Set preview content
          this.previewSubject = previewSubject;
          this.previewBody = previewBody;
        }

        this.showPreview = true;
      } catch (error) {
        // Error preparing preview
      }
    },
    insertVariable(variable) {
      const cursorPos = this.$refs.bodyInput.selectionStart;
      const textBefore = this.body.substring(0, cursorPos);
      const textAfter = this.body.substring(cursorPos);
      this.body = `${textBefore}{{${variable}}}${textAfter}`;
    },
    saveTemplate() {
      // Validate inputs
      if (!this.name.trim() || !this.subject.trim() || !this.body.trim()) {
        alert('Please fill in all fields');
        return;
      }

      const templateData = {
        name: this.name.trim(),
        type: this.type,
        subject: this.subject.trim(),
        body: this.body.trim()
      };
      
      this.$emit('save', templateData);
    }
  }
}
</script> 